import { useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Target } from "lucide-react";
import api from "@/lib/api";

// Read-only "class content" (daily WOD) for a fitness session, visible once the member is enrolled.
export function WorkoutContentButton({ session, enrolled = false, size = "sm", variant = "outline", className = "" }) {
  const { tl } = useI18n();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // null | "loading" | "error" | {date, universe, workout}

  const show = session && session.universe === "fitness" && (enrolled || session.booked_by_me);
  if (!show) return null;

  const openDialog = async () => {
    setOpen(true);
    if (!data || data === "error") {
      setData("loading");
      try {
        const { data: d } = await api.get(`/sessions/${session.session_id}/workout`);
        setData(d);
      } catch { setData("error"); }
    }
  };

  const wod = data && typeof data === "object" ? data.workout : null;

  return (
    <>
      <Button size={size} variant={variant} data-testid={`view-content-${session.session_id}`} onClick={openDialog} className={`rounded-full ${className}`}>
        <BookOpen className="mr-1.5 h-3.5 w-3.5" />
        {tl({ es: "Ver contenido", en: "View content", fr: "Voir le contenu" })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="workout-content-dialog">
          <DialogHeader>
            <DialogTitle>{tl({ es: "Contenido de la clase", en: "Class content", fr: "Contenu du cours" })}</DialogTitle>
          </DialogHeader>

          {data === "loading" && <p className="text-sm text-muted-foreground">{tl({ es: "Cargando…", en: "Loading…", fr: "Chargement…" })}</p>}
          {data === "error" && <p className="text-sm text-destructive">{tl({ es: "No se pudo cargar.", en: "Couldn't load.", fr: "Impossible de charger." })}</p>}

          {data && typeof data === "object" && !wod && (
            <div data-testid="workout-content-empty" className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {tl({ es: "El contenido se publicará pronto.", en: "Content will be published soon.", fr: "Le contenu sera bientôt publié." })}
            </div>
          )}

          {wod && (
            <div className="space-y-4" data-testid="workout-content-body">
              <div>
                <h3 className="font-display text-xl font-bold">{wod.title}</h3>
                {wod.description && <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{wod.description}</p>}
              </div>

              {(wod.focus_tip || wod.focus_video) && (
                <div className="rounded-2xl border border-gold/40 bg-gold/5 p-4">
                  <h4 className="flex items-center gap-2 font-display text-sm font-semibold text-gold">
                    <Target className="h-4 w-4" /> {tl({ es: "Focus del día", en: "Daily Focus", fr: "Focus du jour" })}
                  </h4>
                  {wod.focus_tip && <p className="mt-2 whitespace-pre-wrap text-sm">{wod.focus_tip}</p>}
                  {wod.focus_video && (wod.focus_video.provider === "youtube" || wod.focus_video.provider === "vimeo") && (
                    <div className="mt-3 aspect-video w-full overflow-hidden rounded-xl border border-border">
                      <iframe src={wod.focus_video.embed_url} title={wod.focus_video.title || "video"} className="h-full w-full" allowFullScreen />
                    </div>
                  )}
                </div>
              )}

              {(wod.blocks || []).map((b, i) => (
                <div key={b.block_id} data-testid={`workout-content-block-${b.block_id}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                    <h4 className="font-display font-semibold">{b.title}</h4>
                  </div>
                  {b.description && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{b.description}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
